import { TEST_ID_WATCHLIST } from "e2e/testId";
import { CheckIcon, PlusIcon, SettingsIcon } from "lucide-react-native";
import React, { memo } from "react";

import WatchlistMenuItem from "@/components/molecules/WatchlistMenuItem";

import { Box } from "@/components/atoms/Box";
import { Divider } from "@/components/atoms/Divider";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";
import { WatchlistWatchlistMetaFragment } from "@/generated/graphql";
import { ifWeb } from "@/utils/platform";

/**
 * Data-driven container style: safe-area bottom inset plus measured max-height on native.
 * Hoisted from an inline style object to comply with the closed styling dialect.
 * @param bottomInset - Bottom safe-area inset
 * @param isNative - Whether running on a native platform
 * @param maxHeight - Measured max height for the native dropdown
 */
const getContainerStyle = (
  bottomInset: number,
  isNative: boolean,
  maxHeight: number
) => ({
  paddingBottom: bottomInset,
  maxHeight: isNative ? maxHeight : undefined,
});

/**
 * Props for MenuContentView component
 */
export interface MenuContentViewProps {
  readonly watchlists: WatchlistWatchlistMetaFragment[];
  readonly selectedWatchlistId: string | undefined;
  readonly widthName: number;
  readonly bottomInset: number;
  readonly maxHeight: number;
  readonly isNative: boolean;
  readonly isDark: boolean;
  readonly loading: boolean;
  readonly onWatchlistPress: (id: string) => void;
  readonly onMyRosterPress: () => void;
  readonly onAddWatchlistPress: () => void;
  readonly onManageListsPress: () => void;
}

/**
 * View component for menu content displaying watchlist selection options
 * @param root0 - Component props
 * @param root0.watchlists - Array of available watchlists to display
 * @param root0.selectedWatchlistId - ID of currently selected watchlist
 * @param root0.widthName - Window width for responsive text truncation
 * @param root0.bottomInset - Bottom safe area inset for padding
 * @param root0.maxHeight - Maximum height for dropdown on mobile
 * @param root0.isNative - Whether running on native platform
 * @param root0.isDark - Whether using dark theme styling
 * @param root0.loading - Whether watchlist creation is in progress
 * @param root0.onWatchlistPress - Callback when watchlist item is pressed
 * @param root0.onMyRosterPress - Callback when My Roster option is pressed
 * @param root0.onAddWatchlistPress - Callback when New List option is pressed
 * @param root0.onManageListsPress - Callback when Manage Lists option is pressed
 */
const MenuContentView = ({
  watchlists,
  selectedWatchlistId,
  widthName,
  bottomInset,
  maxHeight,
  isNative,
  isDark,
  loading,
  onWatchlistPress,
  onMyRosterPress,
  onAddWatchlistPress,
  onManageListsPress,
}: MenuContentViewProps) => (
  <Box
    className="w-full"
    // design-system: runtime-value waiver — data-driven safe-area inset + measured max-height
    // eslint-disable-next-line no-restricted-syntax -- compound of runtime safe-area inset + measured maxHeight; not expressible as closed tokens
    UNSAFE_style={getContainerStyle(bottomInset, isNative, maxHeight)}
  >
    <ScrollView
      bounces={false}
      className={isNative ? "" : "flex-1"}
      // eslint-disable-next-line no-restricted-syntax -- measured maxHeight*0.6 on native; runtime geometry, no closed sizing token
      style={isNative ? { maxHeight: maxHeight * 0.6 } : undefined}
      contentContainerStyle={isNative ? undefined : { flexGrow: 1 }}
    >
      <>
        {watchlists.map((watchlist, index) => (
          <WatchlistMenuItem
            key={watchlist.id}
            watchlist={watchlist}
            isSelected={watchlist.id === selectedWatchlistId}
            isFirst={index === 0}
            widthName={widthName}
            onPress={onWatchlistPress}
          />
        ))}
        <Pressable onPress={onMyRosterPress}>
          <Stack
            direction="horizontal"
            className={`items-center gap-3 border-t border-outline-default ${ifWeb("py-3 pl-5 pr-3", "px-4 py-3")}`}
          >
            <Box className="w-4">
              {!selectedWatchlistId ? <Icon as={CheckIcon} /> : null}
            </Box>
            <Box className="flex-1">
              {/* SE-6773: the web arm used to name an `md` size step, for
                  which this theme has no fontSize token — so it compiled to
                  nothing and the element rendered at the Text atom's own
                  default (size "md" -> text-title-sm). Naming that default
                  keeps the render identical. Same for the two below. */}
              <Text
                variant="unstyled"
                className={`font-medium ${ifWeb("text-title-sm", "text-title")}`}
              >
                My Roster
              </Text>
            </Box>
            <Box className="w-6" />
          </Stack>
        </Pressable>
      </>
    </ScrollView>
    <Divider
      className={`h-2 ${isDark ? "bg-typography-black" : "bg-surface-strong"}`}
    />
    <>
      <Pressable
        testID={TEST_ID_WATCHLIST.NEW_LIST_BUTTON}
        disabled={loading}
        onPress={onAddWatchlistPress}
      >
        <Stack
          direction="horizontal"
          className={`items-center gap-3 ${ifWeb("py-3 pl-5 pr-3", "px-4 py-3")}`}
        >
          <Box className="w-4" />
          <Box className="flex-1">
            {/* SE-6773: dead size step -> the atom default it already rendered. */}
            <Text
              variant="unstyled"
              className={`font-medium ${ifWeb("text-title-sm", "text-title")}`}
            >
              {loading ? "Please Wait..." : "New List"}
            </Text>
          </Box>
          <Box className="w-6">
            <Icon as={PlusIcon} />
          </Box>
        </Stack>
      </Pressable>
      <Pressable onPress={onManageListsPress}>
        <Stack
          direction="horizontal"
          className={`items-center gap-3 border-t border-surface-muted ${ifWeb("py-3 pl-5 pr-3", "px-4 py-3")}`}
        >
          <Box className="w-4" />
          <Box className="flex-1">
            {/* SE-6773: dead size step -> the atom default it already rendered. */}
            <Text
              variant="unstyled"
              className={`font-medium ${ifWeb("text-title-sm", "text-title")}`}
            >
              Manage Lists
            </Text>
          </Box>
          <Box className="w-6">
            <Icon as={SettingsIcon} />
          </Box>
        </Stack>
      </Pressable>
    </>
  </Box>
);

MenuContentView.displayName = "MenuContentView";

export default memo(MenuContentView);
